import { createRef, PureComponent } from "react";
import { bool, func, number, object, string } from "prop-types";
import { rem } from "polished";

import {
  FS_CDN,
  FS_WATERMARK,
  FS_WATERMARK_NEW,
  FS_WATERMARK_PENDING,
  FS_WATERMARK_REMOVED,
  FS_WATERMARK_SOLD,
  FS_WATERMARK_SPECIAL,
  FS_WATERMARK_WARRANTY_INCLUDED,
} from "../../constants";

import Picture from "../../styles/components/Picture";

class ResultsPicture extends PureComponent {
  constructor(props) {
    super(props);

    this.getImageSource = this.getImageSource.bind(this);
    this.getObserver = this.getObserver.bind(this);
    this.getPictureTree = this.getPictureTree.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handleLoad = this.handleLoad.bind(this);
    this.renderSources = this.renderSources.bind(this);

    this._isMounted = false;
    this.picture = createRef();

    this.state = {
      img: null,
      sources: null,
      status: "loading",
    };
  }

  /**
   * Get image source path
   * @param {object} dimensions
   * @param {number} dimensions.height
   * @param {number} dimensions.width
   * @param {string} resolution
   */
  getImageSource(dimensions, resolution) {
    const {
      align,
      fit,
      handle,
      includeWarranty,
      newEquipment,
      pending,
      removed,
      sold,
      special,
      watermark,
      watermarkScale,
    } = this.props;
    let params = [FS_CDN, "rotate=deg:exif", "auto_image"];

    if (!!dimensions) {
      let size = "resize=";

      if (!!dimensions.width) {
        size +=
          resolution === "2x"
            ? `width:${dimensions.width * 2}`
            : `width:${dimensions.width}`;
      }

      if (!!dimensions.width && !!dimensions.height) {
        size += ",";
      }

      if (!!dimensions.height) {
        size +=
          resolution === "2x"
            ? `height:${dimensions.height * 2}`
            : `height:${dimensions.height}`;
      }

      if (!!fit) {
        size += `,fit:${fit}`;
      }

      if (!!align) {
        size += `,align:${align}`;
      }

      params = [...params, size];
    }

    if (pending && !!FS_WATERMARK_PENDING) {
      let scalePending = !!dimensions.width ? dimensions.width * 0.01650165 : 5;

      if (!!dimensions.width && !!dimensions.height) {
        const ratio = dimensions.height / dimensions.width;

        if (ratio < 1) {
          scalePending = scalePending * ratio;
        }
      }

      if (resolution === "2x") {
        scalePending = scalePending * 2;
      }

      scalePending = Math.ceil(scalePending);

      params = [
        ...params,
        `watermark=file:${FS_WATERMARK_PENDING},size:${scalePending},position:[middle,center]`,
      ];
    }

    if (removed && !!FS_WATERMARK_REMOVED) {
      let scaleRemoved = !!dimensions.width ? dimensions.width * 0.01650165 : 5;

      if (!!dimensions.width && !!dimensions.height) {
        const ratio = dimensions.height / dimensions.width;

        if (ratio < 1) {
          scaleRemoved = scaleRemoved * ratio;
        }
      }

      if (resolution === "2x") {
        scaleRemoved = scaleRemoved * 2;
      }

      scaleRemoved = Math.ceil(scaleRemoved);

      params = [
        ...params,
        `watermark=file:${FS_WATERMARK_REMOVED},size:${scaleRemoved},position:[middle,center]`,
      ];
    }

    if (sold && !!FS_WATERMARK_SOLD) {
      let scaleSold = !!dimensions.width ? dimensions.width * 0.01650165 : 5;

      if (!!dimensions.width && !!dimensions.height) {
        const ratio = dimensions.height / dimensions.width;

        if (ratio < 1) {
          scaleSold = scaleSold * ratio;
        }
      }

      if (resolution === "2x") {
        scaleSold = scaleSold * 2;
      }

      scaleSold = Math.ceil(scaleSold);

      params = [
        ...params,
        `watermark=file:${FS_WATERMARK_SOLD},size:${scaleSold},position:[middle,center]`,
      ];
    }

    if (special && !!FS_WATERMARK_SPECIAL) {
      const specialOrigSize = 1538;
      const specialTargetPercent = 0.4;
      let scaleSpecial = 9;

      if (!!dimensions.width) {
        let specialTargetSize =
          dimensions.width < 262
            ? dimensions.width * (specialTargetPercent * 2)
            : dimensions.width * specialTargetPercent;

        if (!!dimensions.height && dimensions.height < specialTargetSize) {
          specialTargetSize = dimensions.height * (specialTargetPercent * 2);
        }

        scaleSpecial = 100 * (specialTargetSize / specialOrigSize);
        scaleSpecial = scaleSpecial * ((100 - scaleSpecial) / 100); // Smaller images need slightly larger watermark
        scaleSpecial = Math.floor(scaleSpecial);
      }

      if (resolution === "2x") {
        scaleSpecial = scaleSpecial * 2;
      }

      params = [
        ...params,
        `watermark=file:${FS_WATERMARK_SPECIAL},size:${scaleSpecial},position:[top,left]`,
      ];
    }

    if (newEquipment && !!FS_WATERMARK_NEW) {
      const newOrigSize = 840;
      const newTargetPercent = 0.4;
      let scaleNew = 9;

      if (!!dimensions.width) {
        let newTargetSize =
          dimensions.width < 262
            ? dimensions.width * (newTargetPercent * 2)
            : dimensions.width * newTargetPercent;

        if (!!dimensions.height && dimensions.height < newTargetSize) {
          newTargetSize = dimensions.height * (newTargetPercent * 2);
        }

        scaleNew = 100 * (newTargetSize / newOrigSize);
        scaleNew = scaleNew * ((100 - scaleNew) / 100); // Smaller images need slightly larger watermark
        scaleNew = Math.floor(scaleNew);
      }

      if (resolution === "2x") {
        scaleNew = scaleNew * 2;
      }

      params = [
        ...params,
        `watermark=file:${FS_WATERMARK_NEW},size:${scaleNew},position:[top,left]`,
      ];
    }

    if (includeWarranty && !!FS_WATERMARK_WARRANTY_INCLUDED) {
      let scaleWarrantyIncluded = !!dimensions.width
        ? dimensions.width * 0.035
        : 5;

      if (!!dimensions.width && !!dimensions.height) {
        const ratioWarrantyIncluded = dimensions.height / dimensions.width;
        const offsetWarrantyIncluded = ratioWarrantyIncluded < 0.75 ? 1.5 : 1;

        if (ratioWarrantyIncluded < 1) {
          scaleWarrantyIncluded =
            scaleWarrantyIncluded *
            ratioWarrantyIncluded *
            offsetWarrantyIncluded;
        }
      }

      scaleWarrantyIncluded = Math.floor(scaleWarrantyIncluded);

      if (resolution === "2x") {
        scaleWarrantyIncluded = scaleWarrantyIncluded * 2;
      }

      params = [
        ...params,
        `watermark=file:${FS_WATERMARK_WARRANTY_INCLUDED},size:${scaleWarrantyIncluded},position:[bottom,left]`,
      ];
    }

    if (watermark && !!FS_WATERMARK) {
      let scaleWatermark = watermarkScale;

      if (resolution === "2x") {
        scaleWatermark = scaleWatermark * 2;
      }

      params = [
        ...params,
        `watermark=file:${FS_WATERMARK},size:${scaleWatermark},position:[bottom,right]`,
      ];
    }

    params = [...params, "compress", "cache=expiry:max", handle];

    return params.join("/");
  }

  /**
   * Get intersection observer to defer loading of off-screen images
   * @public
   */
  getObserver() {
    return new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.getPictureTree();
            this.observer = this.observer.disconnect();
          }
        });
      },
      {
        rootMargin: "0px 0px 400px 0px",
      }
    );
  }

  /**
   * Get picture tree
   * @public
   */
  getPictureTree() {
    const { getImageSource } = this;
    const { handle, sizes } = this.props;

    if (!handle || !sizes) {
      this.setState({ img: null, sources: null });
    } else {
      let img = null;
      let sources = [];

      Object.keys(sizes).forEach(size => {
        const media = size !== "0px" ? `(min-width: ${rem(size)})` : null;

        sources = sources.concat({
          key: `${handle}-${size}`,
          media: media,
          srcSet: `${getImageSource(sizes[size], "1x")} 1x, ${getImageSource(
            sizes[size],
            "2x"
          )} 2x`,
        });

        if (!img) {
          img = getImageSource(sizes[size], "1x");
        }
      });

      this.setState({
        img: img,
        sources: sources,
      });
    }
  }

  /**
   * Handle status if image throws an error
   * @public
   */
  handleError(e) {
    if (this._isMounted && this.state.status !== "error") {
      this.setState({ status: "error" });
    }

    if (!!this.props.errorCallback) {
      this.props.errorCallback(e);
    }
  }

  /**
   * Handle status if image loads successfully
   * @public
   */
  handleLoad() {
    if (this._isMounted && this.state.status !== "done") {
      this.setState({ status: "done" });
    }
  }

  /**
   * Render picture sources
   * @param {array} sources
   * @public
   */
  renderSources(sources) {
    return sources.map(sourceObj => {
      return <source {...sourceObj} />;
    });
  }

  componentDidMount() {
    this._isMounted = true;

    if (this.props.lazy) {
      this.observer = this.getObserver();
      this.observer.observe(this.picture.current);
    } else {
      this.getPictureTree();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.props.lazy &&
      !!this.props.handle &&
      prevProps.handle !== this.props.handle
    ) {
      if (!!this.observer) {
        this.observer = this.observer.disconnect();
      }

      this.observer = this.getObserver();
      this.observer.observe(this.picture.current);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (!!this.observer) {
      this.observer = this.observer.disconnect();
    }
  }

  render() {
    const { handleError, handleLoad, picture, renderSources } = this;
    const { alt, className, theme } = this.props;
    const { img, sources, status } = this.state;

    return (
      <Picture
        className={className}
        data-status={status}
        ref={picture}
        theme={theme}
      >
        {!!sources && renderSources(sources)}

        <img
          alt={alt}
          draggable={false}
          onError={handleError}
          onLoad={handleLoad}
          src={img}
        />
      </Picture>
    );
  }
}

ResultsPicture.propTypes = {
  align: string,
  alt: string,
  errorCallback: func,
  fit: string,
  handle: string.isRequired,
  includeWarranty: bool,
  lazy: bool,
  newEquipment: bool,
  pending: bool,
  removed: bool,
  sold: bool,
  sizes: object.isRequired,
  special: bool,
  theme: string,
  transforms: object,
  watermark: bool,
  watermarkScale: number,
};

ResultsPicture.defaultProps = {
  alt: "photo",
  includeWarranty: false,
  lazy: true,
  newEquipment: false,
  pending: false,
  removed: false,
  sold: false,
  special: false,
  watermark: false,
  watermarkScale: 30,
};

export default ResultsPicture;
